import React, { useRef } from "react";
import { Box, Typography, Chip } from "@mui/material";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import {
  MAX_FILE_SIZE_GB,
  MAX_FILES,
} from "../../../services/documentService";

// Theme colors
const COLORS = {
  white: "#fff",
  black: "#000",
  primary: "hsl(19 101% 51% / 1)",
  primaryHover: "hsl(19 101% 41% / 1)",
  primaryLight: "hsl(19 101% 61% / 1)",
};

// Styled components
const UploadBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDragging" && prop !== "compact",
})<{ isDragging: boolean; compact: boolean }>(({ isDragging, compact }) => ({
  border: `2px dashed ${isDragging ? COLORS.primaryLight : COLORS.primary}`,
  borderRadius: "16px",
  padding: compact ? "20px" : "40px",
  textAlign: "center",
  backgroundColor: isDragging ? COLORS.white : "rgba(247, 100, 27, 0.1)",
  transition: "0.3s",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: COLORS.white,
    borderColor: COLORS.white,
    color: COLORS.black,
  },
  "&:focus": {
    outline: `2px solid ${COLORS.primary}`,
    outlineOffset: "2px",
  },
}));

interface FileUploadAreaProps {
  isDragging: boolean;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onFileSelect: (files: FileList) => void;
  primaryText?: string;
  secondaryText?: string;
  dragText?: string;
  dropText?: string;
  browseText?: string;
  accept?: string;
  multiple?: boolean;
  maxInfoLabel?: string;
  compact?: boolean;
  disabled?: boolean;
}

export const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  isDragging,
  onDrop,
  onDragOver,
  onDragLeave,
  onFileSelect,
  primaryText,
  secondaryText,
  dragText = "Drag & drop files here",
  dropText = "Drop files here",
  browseText = "or click to browse files",
  accept = ".pdf,.doc,.docx,.xls,.xlsx,.txt,.heic,.heif,.jpg,.jpeg,.png,.tif,.tiff,.xml,.xmls,.wmv,.mp4,.avi,.mpeg,.zip,.html",
  multiple = true,
  maxInfoLabel,
  compact = false,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelectFiles = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (disabled) {
      return;
    }

    if (e.target.files && e.target.files.length > 0) {
      onFileSelect(e.target.files);
      // Reset input to allow selecting same files again
      e.target.value = "";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) {
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      inputRef.current?.click();
      e.preventDefault();
    }
  };

  const handleBoxClick = () => {
    if (disabled) {
      return;
    }

    inputRef.current?.click();
  };

  return (
    <>
      <Typography
        variant={compact ? "body2" : "body1"}
        color="text.secondary"
        align="center"
        sx={{ mb: compact ? 1.5 : 3 }}
      >
        {primaryText ??
          `Upload documents, images, or videos. Maximum ${MAX_FILE_SIZE_GB} GB per file, ${MAX_FILES} files total. Supported formats: PDF, DOC, DOCX, XLS, XLSX, TXT, HEIC, HEIF, PNG, JPG, JPEG, GIF, TIF, TIFF, XML, XMLS, WMV, MP4, AVI, and MPEG.`}
      </Typography>
      {secondaryText && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mb: compact ? 1.5 : 3 }}
        >
          {secondaryText}
        </Typography>
      )}

      {/* Upload Box */}
      <UploadBox
        isDragging={isDragging}
        compact={compact}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onClick={handleBoxClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="File upload area. Drag and drop files here or click to browse."
        aria-disabled={disabled}
        onKeyDown={handleKeyDown}
        sx={{
          opacity: disabled ? 0.65 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <FontAwesomeIcon
          icon={faUpload}
          size={compact ? "2x" : "3x"}
          color={isDragging ? COLORS.primaryLight : COLORS.primary}
          style={{ marginBottom: compact ? "8px" : "16px" }}
        />
        <Typography
          variant={compact ? "subtitle1" : "h6"}
          sx={{
            mt: 1,
            mb: 1,
            color: isDragging ? COLORS.white : "inherit",
          }}
        >
          {isDragging ? dropText : dragText}
        </Typography>
        <Typography
          variant="body2"
          color={COLORS.black}
          sx={{
            mb: 2,
            color: isDragging ? COLORS.white : "inherit",
          }}
        >
          {browseText}
        </Typography>
        <Chip
          label={maxInfoLabel ?? `Max ${MAX_FILE_SIZE_GB}GB per file • ${MAX_FILES} files max`}
          variant="outlined"
          size="small"
          sx={{
            color: COLORS.primary,
            borderColor: isDragging ? COLORS.white : "inherit",
          }}
        />

        <input
          type="file"
          multiple={multiple}
          hidden
          ref={inputRef}
          onChange={handleSelectFiles}
          accept={accept}
          disabled={disabled}
          aria-label="Select files to upload"
        />
      </UploadBox>
    </>
  );
};
